import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getProduct, updateProduct, createProduct } from '../../config/api';
import { Save, X, ArrowLeft, Pill, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const EditProduct = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // State for form data with all comprehensive pharmaceutical fields
  const [formData, setFormData] = useState({
    // Basic Information
    sku: '', upc: '', name: '', genericName: '', brandName: '', 
    strengthValue: '', strengthUnit: 'mg', strength: '', 
    dosageForm: '', route: '', uom: 'EA',
    
    // NDC Codes
    ndc11: '', ndc10: '', ndc9: '',
    
    // Pharmaceutical Classification
    therapeuticClass: '', atcCode: '', rxRequired: false, controlled: false,
    
    // Pricing
    wac: '', listPrice: '', contractPrice: '', adminFee: '',
    
    // Business Rules
    rebateEligible: true, chargebackEligible: true, contractRequired: false, gpoEligible: true,
    
    // Regulatory
    fdaApprovalDate: '', patentExpiry: '', exclusivityEnd: '', ndaNumber: '', andaNumber: '', bulaVersion: '',
    
    // Comprehensive Product Information
    productId: '', productNdc: '', externalId: '', type: '', description: '', medicaid: false,
    cmsRegistrationName: '', drugCategory: '', baselineAmp: '', drugEnteredMarketDate: '', drugTerminationDate: '',
    
    // Status
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch product data
  const { data: product, isLoading: productLoading, error: productError } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(id),
    enabled: !!id && !isNew && !!user, // don't fetch a product when creating a new one
    retry: false
  });

  // Create/update mutation — routes to the correct API based on mode
  const saveMutation = useMutation({
    mutationFn: ({ id, productData }) => isNew ? createProduct(productData) : updateProduct(id, productData),
    onSuccess: () => {
      toast.success(isNew ? 'Product created successfully' : 'Product updated successfully');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      if (!isNew) queryClient.invalidateQueries({ queryKey: ['product', id] });
      navigate('/app/products');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || `Error ${isNew ? 'creating' : 'updating'} product`);
      setIsSubmitting(false);
    }
  });

  // Populate form when product data loads
  useEffect(() => {
    if (product) {
      // Parse strength field to separate value and unit
      const parseStrength = (strengthStr) => {
        if (!strengthStr) return { value: '', unit: 'mg' };
        const match = strengthStr.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
        if (match) {
          return { value: match[1], unit: match[2].toLowerCase() };
        }
        return { value: strengthStr, unit: 'mg' };
      };
      
      const strengthData = parseStrength(product.strength);
      
      const newFormData = {
        sku: product.sku || '', upc: product.upc || '', name: product.name || '',
        genericName: product.genericName || '', brandName: product.brandName || '', 
        strengthValue: strengthData.value, strengthUnit: strengthData.unit, strength: product.strength || '',
        dosageForm: product.dosageForm || '', route: product.route || '', uom: product.uom || 'EA',
        ndc11: product.ndc11 || '', ndc10: product.ndc10 || '', ndc9: product.ndc9 || '',
        therapeuticClass: product.therapeuticClass || '', atcCode: product.atcCode || '',
        rxRequired: product.rxRequired || false, controlled: product.controlled || false,
        wac: product.wac ? product.wac.toString() : '', listPrice: product.listPrice ? product.listPrice.toString() : '',
        contractPrice: product.contractPrice ? product.contractPrice.toString() : '', adminFee: product.adminFee ? product.adminFee.toString() : '',
        rebateEligible: product.rebateEligible !== undefined ? product.rebateEligible : true,
        chargebackEligible: product.chargebackEligible !== undefined ? product.chargebackEligible : true,
        contractRequired: product.contractRequired || false, gpoEligible: product.gpoEligible !== undefined ? product.gpoEligible : true,
        fdaApprovalDate: product.fdaApprovalDate ? product.fdaApprovalDate.split('T')[0] : '',
        patentExpiry: product.patentExpiry ? product.patentExpiry.split('T')[0] : '',
        exclusivityEnd: product.exclusivityEnd ? product.exclusivityEnd.split('T')[0] : '',
        ndaNumber: product.ndaNumber || '', andaNumber: product.andaNumber || '', bulaVersion: product.bulaVersion || '',
        productId: product.productId || '', productNdc: product.productNdc || '', externalId: product.externalId || '',
        type: product.type || '', description: product.description || '', medicaid: product.medicaid || false,
        cmsRegistrationName: product.cmsRegistrationName || '', drugCategory: product.drugCategory || '',
        baselineAmp: product.baselineAmp ? product.baselineAmp.toString() : '',
        drugEnteredMarketDate: product.drugEnteredMarketDate ? product.drugEnteredMarketDate.split('T')[0] : '',
        drugTerminationDate: product.drugTerminationDate ? product.drugTerminationDate.split('T')[0] : '',
        isActive: product.isActive !== undefined ? product.isActive : true
      };
      setFormData(newFormData);
    }
  }, [product]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle strength field changes
    if (name === 'strengthValue' || name === 'strengthUnit') {
      setFormData(prev => {
        const newData = { ...prev, [name]: value };
        // Combine strength value and unit
        if (newData.strengthValue && newData.strengthUnit) {
          newData.strength = `${newData.strengthValue} ${newData.strengthUnit}`;
        } else if (newData.strengthValue) {
          newData.strength = newData.strengthValue;
        } else {
          newData.strength = '';
        }
        return newData;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.sku?.trim()) newErrors.sku = 'SKU is required';
    if (!formData.name?.trim()) newErrors.name = 'Product name is required';
    if (!formData.uom?.trim()) newErrors.uom = 'Unit of measure is required';
    
    // Validate strength field
    if (formData.strengthValue && !/^\d+(\.\d+)?$/.test(formData.strengthValue)) {
      newErrors.strengthValue = 'Strength value must be a valid number';
    }
    if (formData.strengthValue && parseFloat(formData.strengthValue) <= 0) {
      newErrors.strengthValue = 'Strength value must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    const submitData = { ...formData };
    Object.keys(submitData).forEach(key => { if (submitData[key] === '') submitData[key] = null; });

    // Convert decimal fields
    ['wac', 'listPrice', 'contractPrice', 'adminFee', 'baselineAmp'].forEach(field => {
      if (submitData[field]) submitData[field] = parseFloat(submitData[field]);
    });

    // Convert date fields
    ['fdaApprovalDate', 'patentExpiry', 'exclusivityEnd', 'drugEnteredMarketDate', 'drugTerminationDate'].forEach(field => {
      if (submitData[field]) submitData[field] = new Date(submitData[field]).toISOString();
    });

    saveMutation.mutate({ id, productData: submitData });
  };

  // Auth check
  if (!user || !['ADMIN', 'STAFF'].includes(user.roles?.[0])) {
    navigate('/products');
    return null;
  }

  if (productLoading) {
    return <div className="flex justify-center items-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>;
  }

  if (productError) {
    return <div className="p-6"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><div className="flex items-center"><AlertCircle className="h-5 w-5 text-red-400 mr-2" /><span className="text-red-800">Error loading product: {productError.message}</span></div></div></div>;
  }

  if (!product) {
    return <div className="p-6"><div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><div className="flex items-center"><AlertCircle className="h-5 w-5 text-yellow-400 mr-2" /><span className="text-yellow-800">Product not found</span></div></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app/products')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg" title="Back to Products">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'Add Product' : 'Edit Product'}</h1>
            <p className="text-gray-600">Update comprehensive pharmaceutical product information</p>
          </div>
        </div>
        <Pill className="h-8 w-8 text-primary-600" />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Product Information</h2>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                <input type="text" name="sku" value={formData.sku} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.sku ? 'border-red-300' : 'border-gray-300'}`} placeholder="Enter SKU" />
                {errors.sku && <p className="mt-1 text-sm text-red-600">{errors.sku}</p>}
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">UPC Code</label>
                <input type="text" name="upc" value={formData.upc} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Enter UPC code" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.name ? 'border-red-300' : 'border-gray-300'}`} placeholder="Enter product name" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Generic Name</label>
                <input type="text" name="genericName" value={formData.genericName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Enter generic name" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Brand Name</label>
                <input type="text" name="brandName" value={formData.brandName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Enter brand name" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Strength</label>
                <div className="flex gap-2">
                  <input type="number" step="0.01" name="strengthValue" value={formData.strengthValue} onChange={handleInputChange} className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.strengthValue ? 'border-red-300' : 'border-gray-300'}`} placeholder="500" />
                  <select name="strengthUnit" value={formData.strengthUnit} onChange={handleInputChange} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                    <option value="mg">mg</option>
                    <option value="mcg">mcg</option>
                    <option value="g">g</option>
                    <option value="ml">ml</option>
                    <option value="units">units</option>
                    <option value="IU">IU</option>
                    <option value="mEq">mEq</option>
                    <option value="mmol">mmol</option>
                    <option value="%">%</option>
                    <option value="mcg/mL">mcg/mL</option>
                    <option value="mg/mL">mg/mL</option>
                    <option value="units/mL">units/mL</option>
                    <option value="mg/5mL">mg/5mL</option>
                    <option value="mg/10mL">mg/10mL</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {errors.strengthValue && <p className="mt-1 text-sm text-red-600">{errors.strengthValue}</p>}
                <p className="mt-1 text-xs text-gray-500">Combined: {formData.strength || 'No strength specified'}</p>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Dosage Form</label>
                <select name="dosageForm" value={formData.dosageForm} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select dosage form</option>
                  <option value="TABLET">Tablet</option><option value="CAPSULE">Capsule</option><option value="LIQUID">Liquid</option>
                  <option value="INJECTION">Injection</option><option value="TOPICAL">Topical</option><option value="INHALATION">Inhalation</option>
                  <option value="TRANSDERMAL">Transdermal</option><option value="OPHTHALMIC">Ophthalmic</option><option value="OTIC">Otic</option><option value="NASAL">Nasal</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Route</label>
                <select name="route" value={formData.route} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select route</option>
                  <option value="ORAL">Oral</option><option value="INTRAVENOUS">Intravenous</option><option value="INTRAMUSCULAR">Intramuscular</option>
                  <option value="SUBCUTANEOUS">Subcutaneous</option><option value="TOPICAL">Topical</option><option value="INHALATION">Inhalation</option>
                  <option value="TRANSDERMAL">Transdermal</option><option value="OPHTHALMIC">Ophthalmic</option><option value="OTIC">Otic</option><option value="NASAL">Nasal</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Unit of Measure *</label>
                <select name="uom" value={formData.uom} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.uom ? 'border-red-300' : 'border-gray-300'}`}>
                  <option value="EA">Each (EA)</option><option value="BOX">Box (BOX)</option><option value="CASE">Case (CASE)</option>
                  <option value="BOTTLE">Bottle (BOTTLE)</option><option value="VIAL">Vial (VIAL)</option><option value="SYRINGE">Syringe (SYRINGE)</option>
                  <option value="PACK">Pack (PACK)</option><option value="KIT">Kit (KIT)</option><option value="UNIT">Unit (UNIT)</option><option value="ML">Milliliters (ML)</option>
                </select>
                {errors.uom && <p className="mt-1 text-sm text-red-600">{errors.uom}</p>}
              </div>
            </div>
          </div>

          {/* NDC Codes */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">NDC Codes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">NDC-11</label>
                <input type="text" name="ndc11" value={formData.ndc11} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="11-digit NDC" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">NDC-10</label>
                <input type="text" name="ndc10" value={formData.ndc10} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="10-digit NDC" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">NDC-9</label>
                <input type="text" name="ndc9" value={formData.ndc9} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="9-digit NDC" />
              </div>
            </div>
          </div>

          {/* Pharmaceutical Classification */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pharmaceutical Classification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Therapeutic Class</label>
                <select name="therapeuticClass" value={formData.therapeuticClass} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select therapeutic class</option>
                  <option value="ANALGESICS">Analgesics</option><option value="ANTIBIOTICS">Antibiotics</option><option value="ANTIHYPERTENSIVES">Antihypertensives</option>
                  <option value="ANTIDIABETICS">Antidiabetics</option><option value="ANTICOAGULANTS">Anticoagulants</option><option value="STATINS">Statins</option>
                  <option value="ONCOLOGY">Oncology</option><option value="IMMUNOSUPPRESSANTS">Immunosuppressants</option><option value="VACCINES">Vaccines</option><option value="HORMONES">Hormones</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">ATC Code</label>
                <input type="text" name="atcCode" value={formData.atcCode} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Anatomical Therapeutic Chemical code" />
              </div>
              <div className="flex items-center space-x-6">
                <label className="flex items-center"><input type="checkbox" name="rxRequired" checked={formData.rxRequired} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Prescription Required</span></label>
                <label className="flex items-center"><input type="checkbox" name="controlled" checked={formData.controlled} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Controlled Substance</span></label>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">WAC Price</label>
                <input type="number" step="0.01" name="wac" value={formData.wac} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0.00" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">List Price</label>
                <input type="number" step="0.01" name="listPrice" value={formData.listPrice} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0.00" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Contract Price</label>
                <input type="number" step="0.01" name="contractPrice" value={formData.contractPrice} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0.00" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Admin Fee</label>
                <input type="number" step="0.01" name="adminFee" value={formData.adminFee} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0.00" />
              </div>
            </div>
          </div>

          {/* Business Rules */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Business Rules</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <label className="flex items-center"><input type="checkbox" name="rebateEligible" checked={formData.rebateEligible} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Rebate Eligible</span></label>
              <label className="flex items-center"><input type="checkbox" name="chargebackEligible" checked={formData.chargebackEligible} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Chargeback Eligible</span></label>
              <label className="flex items-center"><input type="checkbox" name="contractRequired" checked={formData.contractRequired} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Contract Required</span></label>
              <label className="flex items-center"><input type="checkbox" name="gpoEligible" checked={formData.gpoEligible} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">GPO Eligible</span></label>
            </div>
          </div>

          {/* Regulatory Information */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Regulatory Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">FDA Approval Date</label>
                <input type="date" name="fdaApprovalDate" value={formData.fdaApprovalDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Patent Expiry</label>
                <input type="date" name="patentExpiry" value={formData.patentExpiry} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Exclusivity End</label>
                <input type="date" name="exclusivityEnd" value={formData.exclusivityEnd} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">NDA Number</label>
                <input type="text" name="ndaNumber" value={formData.ndaNumber} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="New Drug Application number" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">ANDA Number</label>
                <input type="text" name="andaNumber" value={formData.andaNumber} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Abbreviated New Drug Application number" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">BULA Version</label>
                <input type="text" name="bulaVersion" value={formData.bulaVersion} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Package insert version" />
              </div>
            </div>
          </div>

          {/* Comprehensive Product Information */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Comprehensive Product Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Product ID</label>
                <input type="text" name="productId" value={formData.productId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="External product ID" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Product NDC</label>
                <input type="text" name="productNdc" value={formData.productNdc} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Product NDC" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">External ID</label>
                <input type="text" name="externalId" value={formData.externalId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="External system ID" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
                <input type="text" name="type" value={formData.type} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., PRESCRIPTION, OTC, SUPPLEMENT" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">CMS Registration Name</label>
                <input type="text" name="cmsRegistrationName" value={formData.cmsRegistrationName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="CMS registration name" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Drug Category</label>
                <input type="text" name="drugCategory" value={formData.drugCategory} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Drug category" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Baseline AMP</label>
                <input type="number" step="0.01" name="baselineAmp" value={formData.baselineAmp} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0.00" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Market Entry Date</label>
                <input type="date" name="drugEnteredMarketDate" value={formData.drugEnteredMarketDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Termination Date</label>
                <input type="date" name="drugTerminationDate" value={formData.drugTerminationDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div className="md:col-span-2 lg:col-span-3"><label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Product description" />
              </div>
              <div className="flex items-center"><label className="flex items-center"><input type="checkbox" name="medicaid" checked={formData.medicaid} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Medicaid Eligible</span></label></div>
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Status</h3>
            <div className="flex items-center space-x-6">
              <label className="flex items-center"><input type="radio" name="isActive" value={true} checked={formData.isActive === true} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Active</span></label>
              <label className="flex items-center"><input type="radio" name="isActive" value={false} checked={formData.isActive === false} onChange={handleInputChange} className="mr-2 text-primary-600 focus:ring-primary-500" /><span className="text-sm text-gray-700">Inactive</span></label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/app/products')} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-transparent flex items-center gap-2" title="Cancel">
            <X size={16} />Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" title="Save Changes">
            {isSubmitting ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Saving...</>) : (<><Save size={16} />Save Changes</>)}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProduct;
